import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { LoginEmailInput } from '@/components/login/LoginEmailInput';
import { LoginPasswordInput } from '@/components/login/LoginPasswordInput';
import { fetchLogin } from '@/lib/login';
import { sendSentryMessage } from '@/lib/sentry/sentry';
import { type AlterAuthFormData, CUSTOM_ERROR_MESSAGES } from '@/models/login.model';
import { sendExceptionAnalytics, sendLoginAnalytics } from '@/utils/analytics';
import { _getPath } from '@/utils/helpers/get-path';

interface LoginTraditionalProps {
  className?: string;
}

export const LoginTraditional: React.FC<LoginTraditionalProps> = ({ className }) => {
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { query } = router;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AlterAuthFormData>({});

  const onSubmit = async (data: AlterAuthFormData) => {
    setLoading(true);
    const res = await fetchLogin(data);
    if (res.ok) {
      _handleLoginSuccess(data);
    } else {
      _handleLoginError(res);
    }
  };

  const _handleLoginSuccess = useCallback(
    (data: AlterAuthFormData) => {
      sendLoginAnalytics(data?.email);
      localStorage.removeItem('kitId');
      localStorage.removeItem('activation_code');
      setLoading(false);
      const from = searchParams.get('from') ?? '/portal';
      const [fromPath, search] = from.split('?');
      const path = _getPath(query, fromPath);
      return router.push({
        pathname: path,
        search: search ?? '',
      });
    },
    [query, router, searchParams]
  );

  const _handleLoginError = useCallback(
    async (res: Response) => {
      setLoading(false);
      sendExceptionAnalytics('login error');
      sendSentryMessage('login error');
      const response = await res.json();
      const errorMessage =
        CUSTOM_ERROR_MESSAGES[response?.error_description as keyof typeof CUSTOM_ERROR_MESSAGES] ??
        response?.error_description;
      setError('password', { message: errorMessage });
    },
    [setError]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-[16px] mt-6 break-words lg:mt-10">
        <LoginEmailInput register={register} errors={errors}></LoginEmailInput>
        <LoginPasswordInput register={register} errors={errors} />
      </div>

      <button
        type="submit"
        data-testid="loginButton"
        className="button w-full mt-6 mb-2 font-light text-lg"
      >
        {loading ? 'Loading' : 'Log in'}
      </button>
    </form>
  );
};
